// src/App.jsx — thin shell: auth gate → pairing or the chat app.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { setSelfUid } from './services/messageOrder';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config/firebase';
import { registerDevice, retireThisDevice } from './crypto/identity';
import { getUserProfile } from './services/users';
import { watchChats, lastActivityMs } from './services/chats';
import { watchBlocking, EMPTY_BLOCKING } from './services/blocking';
import { watchChatLocks } from './services/chatLock';
import ChatLockGate from './ui/ChatLockGate';
import { watchMyReads } from './services/receipts';
import { requestHistory , onRetained } from './services/historySync';
import { drain as drainInbox, clear as clearInbox } from './services/deviceInbox';
import { initSocket, closeSocket } from './services/socket';
import { startSeqSync, stopSeqSync } from './services/seqSync'; // 🔢 persist the server's order number
import {
  verifySession, watchSession, writeSession, startActivityHeartbeat, dropSession, consumeQrLogin,
} from './services/session';
import QRLogin from './ui/QRLogin';
import LoginPending from './ui/LoginPending';
import BootGreetings from './ui/BootGreetings';
import { approvedKeyFor } from './services/sessionGate';
import Sidebar from './ui/Sidebar';
import ChatWindow from './ui/ChatWindow';
import ForwardModal from './ui/ForwardModal';
import WelcomePane from './ui/WelcomePane';
import NavRail from './ui/NavRail';
import CallsPanel from './ui/CallsPanel';
import ProfilePanel from './ui/ProfilePanel';
import VibesScreen from './ui/VibesScreen';
import './ui/vibes.css';
import './ui/avatar3d.css';
/* 🎨 the media editor's own sheet — a phone frame, its chrome and a tool
   panel, all `mx-` so nothing in it can collide with the feed's `vb-`;
   see ui/MediaEditor.jsx */
import './ui/mediaEditor.css';
import Resizer, { restoreSize } from './ui/Resizer';
import MediaViewer from './ui/MediaViewer';
import ErrorBoundary from './ui/ErrorBoundary';
import CallDock from './ui/CallDock';
import CallRatingCard from './ui/CallRatingCard';
import { clearLastEndedCall } from './services/calls';
import { attachCalls, subscribeCalls, getCallState } from './services/calls';
import { attachGroupCalls } from './services/groupCalls';
import GroupCallDock from './ui/GroupCallDock';
import { attachCallFeed, subscribeCallFeed, markCallsSeen, missedUnseenCount } from './services/callFeed';
import {
  attachNotify, setNotifyContext, notifyIncomingCall, stopIncomingNotify,
} from './services/notify';
import { relockItems as pmRelockItems } from './services/privateMedia';
import { unreadFor } from './services/chats';
import { watchNotifications } from './services/vibes';
import GuestApp from './ui/GuestApp';
import SupportApp from './ui/SupportApp';   // 💛 Support AlloApp
import { guestTokenFromLocation } from './services/guestSession';
import CookieBanner, { CookieSettings } from './ui/CookieBanner';
import { pageview } from './services/analytics';
import Home from './ui/Home';
import About from './ui/About';
import { wipeLocalData } from './services/wipeLocal';
import { wipeOnClose } from './services/sharedMachine';
import { startPlaintextGuard } from './services/plaintextGuard';
import VaultGate from './ui/VaultGate';
import { isEnrolled, isUnlocked, forgetVault } from './crypto/vault';
import { forgetSelfIdentity } from './services/selfIdentityWeb';
import {
  HOME, ABOUT, APP, routeFromPath, onRouteChange, navigate, ROUTE_TITLES,
} from './services/siteRoute';
import './ui/theme.css';
import './ui/media.css';
import './ui/calls.css';
/* 🟥 LAST, AND IT HAS TO STAY LAST. mobile.css is the whole phone layout in one
   file, and it wins its ties with theme/media/vibes/pairing by being later in
   the cascade rather than by shouting !important. Move this import above any of
   them and rules like the single-pane shell silently stop applying. */
import './ui/mobile.css';

/* 🔗 GUEST LINK — /g/<token> is a PUBLIC surface, decided here, ABOVE the app.

   🟥 IT CANNOT LIVE BEHIND THE GATE INSIDE AlloApp. A guest has no Allo
      account, so the auth listener would put them on a login screen and the
      approval flow would wait forever for a phone they do not own.
   🟥 AND IT CANNOT BE AN EARLY RETURN INSIDE AlloApp EITHER — that would make
      every hook below it conditional, which React forbids and which is exactly
      the "rendered more hooks than during the previous render" class of bug.
      A separate component means the signed-in app never mounts for a guest at
      all: no socket, no chat list, no history sync, and no login flash. */
export default function App() {
  /* 🟥 THE GUEST ROUTE RETURNS BEFORE ANALYTICS EXISTS. A guest URL carries the
     token that IS the conversation's password, so nothing that reports a page
     path may run on it — not the banner, not gtag, not a beacon. Returning here
     means the analytics module is never even reached on that route, which is the
     outermost of the three guards described in services/analytics.js. */
  if (guestTokenFromLocation()) return <GuestApp />;
  /* 💛 allloapp.com/support — "Support AlloApp" (SupportApp.jsx). Its own page, above the site: its URL can carry
     a one-time sign-in code from the app, so like a guest link it returns before analytics exists. */
  if (/^\/support\/?$/.test(window.location.pathname)) return <SupportApp />;
  return (
    <>
      <Site />
      <CookieBanner />
      <CookieSettings />
    </>
  );
}

/**
 * The three public URLs — /, /about and /app — and the decision of which one a
 * visitor is looking at.
 *
 * 🟥 THE HOOKS LIVE HERE, NOT IN App(). App() early-returns for a guest before
 *    any hook runs, which is only legal because it has none. Adding state to
 *    App() would make every hook in it conditional on the URL and produce the
 *    "rendered more hooks than during the previous render" crash the moment a
 *    guest link was opened. This component is what keeps that early return safe.
 */
function Site() {
  const [route, setRoute] = useState(() => routeFromPath());
  useEffect(() => onRouteChange(setRoute), []);

  const isSite = route === HOME || route === ABOUT;

  /* 🟥 THE APP IS A FIXED 100vh SHELL AND THESE PAGES ARE DOCUMENTS. theme.css
     pins html/body/#root to height:100%; site.css releases that, but only under
     `html.site-page`, so the class has to go on and come off with the route. */
  useEffect(() => {
    document.documentElement.classList.toggle('site-page', isSite);
    return () => document.documentElement.classList.remove('site-page');
  }, [isSite]);

  useEffect(() => {
    document.title = ROUTE_TITLES[route] || 'Allo for Web';
    if (!isSite) return;
    /* An in-page navigation keeps the browser's scroll position, which on a page
       this long means arriving at About somewhere near its footer. */
    window.scrollTo(0, 0);
    pageview(route === ABOUT ? 'About' : 'Home', route === ABOUT ? '/about' : '/');
  }, [route, isSite]);

  /* 🟢 A SIGNED-IN VISITOR AT `/` GETS THEIR CHATS, NOT THE PITCH. Home is the
     front door for people who have not seen Allo; making the person who uses it
     every day click past their own product would be a daily tax for a one-time
     benefit. It fires ONCE, on the first answer from Firebase, and only when the
     URL is still `/` — so choosing Features from the header later stays put, and
     a deliberate visit to /about is never hijacked. replace: true keeps Back
     pointing at wherever they came from rather than at a page that bounces. */
  const bounced = useRef(false);
  useEffect(() => onAuthStateChanged(auth, (u) => {
    if (bounced.current) return;
    bounced.current = true;
    if (u && routeFromPath() === HOME) setRoute(navigate(APP, { replace: true }));
  }), []);

  if (route === ABOUT) return <About />;
  if (route === HOME) return <Home />;
  return <AlloApp />;
}

function AlloApp() {
  const [user, setUser] = useState(undefined); // undefined = loading, null = signed out
  /**
   * 🟥 SIGNED IN ≠ LET IN. Firebase hands back a session the moment a password checks
   *    out, but the account's rule — the same one App.tsx enforces on a new handset — is
   *    that a device Allo has not met waits for a trusted device to approve it. This is
   *    that gate: 'checking' while we look up our own session row, 'approve' while the
   *    person approves from their phone (or types an emailed code), 'ok' once admitted.
   *    Nothing below may mount until it reads 'ok': no socket, no history request, no
   *    chat list. Rendering the app "just briefly" while approval is pending would leak
   *    exactly what the gate exists to withhold.
   */
  const [gate, setGate] = useState('checking');
  /* 🟢 null = nothing to ask; 'unlock' = enrolled and the key has been dropped;
     'enroll' = offered once, and declined for this tab if they say Not now. */
  const [vaultAsk, setVaultAsk] = useState(null);
  /* 🟢 MY OWN READ POSITION PER CHAT, from users/{me}/chatReads — written by every
     device I own on every read, receipts on or off. It vetoes a badge that
     `readUpTo` can't, because `readUpTo` is a receipt and stays unwritten when
     receipts are off. See services/receipts.js for the whole story. */
  const [myReads, setMyReads] = useState({});
  const [myProfile, setMyProfile] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  // 🟢 the second pane. Null almost always; a chat only lands here by being dragged out
  //    of the list onto an open conversation.
  const [secondChat, setSecondChat] = useState(null);
  const shellRef = useRef(null);
  const splitRef = useRef(null);

  // put the splitter back where it was left, before the first paint
  useEffect(() => {
    restoreSize(shellRef.current, '--sidebar-w', 'allo_sidebar_w');
    restoreSize(shellRef.current, '--split-a', 'allo_split_a');
  }, [user]);

  // one source of truth for "is there room for two conversations", so the rows stop
  // being draggable at the same moment a drop would be refused
  const [wide, setWide] = useState(() => window.innerWidth >= 1100);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 1100);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 🟥 SPLIT VIEW NEEDS ROOM. Two conversations plus the list is ~1100px before either
  //    side starts truncating names and wrapping bubbles into columns two words wide.
  //    Below that the second pane is dropped rather than squeezed — and it's dropped on
  //    RESIZE too, so dragging the window narrow doesn't leave an unusable layout.
  useEffect(() => { if (secondChat && !wide) setSecondChat(null); }, [wide, secondChat]);

  /**
   * A chat row was dropped on a conversation.
   *   • on the ONLY pane   → open the dropped chat beside it (that's the feature)
   *   • on one of TWO panes → that pane switches to the dropped chat, because "drop it
   *     here" plainly means "put it here", not "put it somewhere else"
   */
  const dropChat = (chatId, slot) => {
    const c = chats.find((x) => x.id === chatId);
    if (!c || !wide) return;
    if (slot === 'single') { if (c.id !== selectedChat?.id) setSecondChat(c); return; }
    if (slot === 'a') { if (c.id !== secondChat?.id) setSelectedChat(c); return; }
    if (c.id !== selectedChat?.id) setSecondChat(c);
  };
  const [forwarding, setForwarding] = useState(null);
  // 🟢 The viewer takes a RICH payload now — {items,index,senderName,timestamp,mine,
  //    caption} — so an album opens at the tile you clicked and the header can name
  //    who sent it and when (ChatWindow.openMedia builds it).
  const [viewer, setViewer] = useState(null);
  const [syncedHistory, setSyncedHistory] = useState(null);
  /* 🟥 THE ARCHIVE GROWS WITHOUT A SYNC NOW. retainLive writes what arrived live
     into the durable store the moment it appears — and this state is what the
     conversation actually renders from, so without hearing about it a message
     would be safely on disk and still invisible until the next sync. Which was
     the whole complaint. */
  useEffect(() => onRetained((store) => setSyncedHistory((prev) => ({ ...(prev || {}), ...store }))), []);
  const [syncStatus, setSyncStatus] = useState('');
  const [syncProgress, setSyncProgress] = useState(null);   // { pct?, text } — real numbers

  // auth listener → decide whether this browser is admitted
  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      /* 🟥 ORDERING NEEDS TO KNOW WHOSE MESSAGES ARE MINE, AND IT NEEDS TO KNOW
         FIRST. Messages are keyed to arrival: my own rows to my send time,
         incoming rows to when this client received them. With no self uid every
         row looks incoming, so my own sends would be keyed to "now" — and a send
         whose serverTimestamp has not resolved yet would be keyed to a receive
         time it never had. This listener is the earliest point the uid exists and
         it fires before any chat is opened or any history syncs, which is the
         only reason it is safe to leave the setter out of the watchers. */
      try { setSelfUid(u?.uid || null); } catch { /* ordering falls back to time */ }
      if (!u) {
        closeSocket();
        setGate('checking');
        setMyProfile(null); setChats([]); setSelectedChat(null); setSecondChat(null);
        return;
      }
      // A QR pairing is itself an approval — the phone was unlocked, signed in and
      // pointed at this screen. pairing.js flags it; we clear the flag and let it in.
      if (consumeQrLogin()) {
        await writeSession(u.uid, { trusted: true, method: 'qr' }).catch(() => {});
        setGate('ok');
        return;
      }
      /* 🟢 A REMEMBERED APPROVAL OPENS THE DOOR NOW — the same rule as the phone.
         The server check still runs, and its 'approve' / 'kicked' still takes
         effect below; the boot screen is for a browser the app has never let in. */
      let remembered = false;
      try {
        remembered = localStorage.getItem(approvedKeyFor(u.uid)) === '1' || localStorage.getItem('allo_web_trusted') === '1';
      } catch { remembered = false; }
      if (remembered) setGate('ok');
      const verdict = await verifySession(u.uid);
      if (verdict === 'kicked') {
        // The phone logged this browser out while it was closed. verifySession has
        // already removed the row and signed us out; the listener will fire again.
        setGate('checking');
        return;
      }
      setGate(verdict === 'trusted' ? 'ok' : 'approve');
    });
  }, []);

  // everything that assumes an admitted session lives here, keyed on the gate
  /* 🟢 bumped when the archive was dropped for idleness AND somebody has come
     back — see startPlaintextGuard. It is a dependency of the sync effect, so
     returning re-asks the phone instead of leaving an empty list until reload. */
  const [historyEpoch, setHistoryEpoch] = useState(0);
  useEffect(() => {
    if (!user || gate !== 'ok') return undefined;
    let alive = true;
    registerDevice(user.uid).catch(() => {});
    // 🟢 hand the socket our deviceId so the server's per-device call routing
    //    (ring dismissal, answered-elsewhere, call switching) can address this browser
    import('./crypto/identity').then(({ getIdentity }) => getIdentity())
      .then((id) => initSocket(user.uid, id?.deviceId))
      .catch(() => initSocket(user.uid));
    /* 🔢 THE ORDER NUMBER. The server hands each message a per-chat `seq` — the
       one key both clients sort by — and reports it back without writing it to
       the document; every sender persists its own. The phone always has; the
       web never did, which is why messages sent from here landed in the app
       with no seq and drifted around the list. */
    startSeqSync();
    getUserProfile(user.uid).then((p) => { if (alive) setMyProfile(p); });
    // the phone's Active Sessions row shows "last active"; keep it honest
    /* 🟥 WAS `setInterval(touchSession, 5min)` WITH NOTHING ASKING IF ANYONE WAS
       THERE. A tab left open on a desk reported itself active for ever, so the
       idle expiry could never come due — the feed defeating the control it fed.
       startActivityHeartbeat beats only when there has been real input. */
    const beat = startActivityHeartbeat(user.uid);
    /* 🟥 AND ON A SHARED MACHINE, NOTHING SURVIVES THE TAB. The pairing question
       promised exactly this; without it the promise is decoration. */
    const stopWipe = wipeOnClose(() => { wipeLocalData(); });
    /* 🟥 AND THE ARCHIVE FORGETS ITSELF LONG BEFORE THE SESSION DOES. A laptop
       left at home held a decrypted history for the full fourteen days of the
       session, because the wipe was tied to the logout — and IndexedDB is
       readable whether anyone is signed in or not. This drops it after a day
       and leaves the login alone. */
    const stopGuard = startPlaintextGuard(() => setHistoryEpoch((e) => e + 1));
    /* 🟥 THE LOCK IS ONLY WORTH ANYTHING IF SOMETHING NOTICES IT. The guard drops
       the key on idle; without this the next read would simply throw somewhere
       deep in a cache and the screen would show a broken image rather than the
       one question that fixes it. */
    const vaultTick = setInterval(async () => {
      if (await isEnrolled().catch(() => false)) {
        setVaultAsk(isUnlocked() ? null : 'unlock');
      }
    }, 15 * 1000);
    /* 🟢 OFFERED ONCE, AND "NOT NOW" IS REMEMBERED. A security prompt that
       reappears every session is one people learn to dismiss without reading,
       which is worse than never having asked. */
    (async () => {
      if (await isEnrolled().catch(() => false)) return;
      let asked = false;
      try { asked = localStorage.getItem('allo_vault_offered') === '1'; } catch { asked = true; }
      if (asked) return;
      try { localStorage.setItem('allo_vault_offered', '1'); } catch { /* private mode */ }
      if (alive) setVaultAsk('enroll');
    })();
    // 🟢 the remote kill switch: the phone sets forceLogout on our session doc and we
    //    sign ourselves out, exactly as a second handset does.
    const stopWatch = watchSession(user.uid, () => setGate('checking'));
    return () => { alive = false; beat(); stopWipe(); stopGuard(); clearInterval(vaultTick); stopWatch(); stopSeqSync(); };
  }, [user, gate]);

  // chat list
  useEffect(() => {
    if (!user || gate !== 'ok') return;
    const unsub = watchChats(user.uid, setChats);
    return () => unsub && unsub();
  }, [user, gate]);

  // ── which section the nav rail has open, and its hide-on-scroll state ──
  const [view, setView] = useState('chats');       // chats | vibes | calls | profile

  /* 📊 ONE PAGEVIEW PER SCREEN, and one for the signed-out landing.
     🟢 The section is a VIRTUAL path — this client is a single HTML page whose
        `view` is state, not a URL, so without it every screen in the app would
        be reported as "/" and the property would show one page and nothing
        about which parts people actually use.
     🟥 The uid is deliberately NOT sent. GA offers a user_id field and filling
        it would turn a visit count into a per-person activity log tied to an
        account — a different thing from what the banner asked permission for. */
  useEffect(() => {
    if (user === undefined) return;                 // still deciding
    /* 🟥 '/app', NOT '/'. The signed-out app screen used to be what lived at the
       root, so reporting it as '/' was right. Home lives there now, and leaving
       this as '/' would merge two entirely different pages — the marketing page
       and the QR login — into one row that means nothing. */
    if (user === null) { pageview('Sign in', '/app'); return; }
    if (gate !== 'ok') { pageview('Approve this device', '/approve'); return; }
    const NAMES = { chats: 'Chats', vibes: 'Vibes', calls: 'Calls', profile: 'Profile' };
    pageview(NAMES[view] || 'Allo', `/app/${view}`);
  }, [user, gate, view]);
  const [navHidden, setNavHidden] = useState(false);
  // scrolled away in one section, then switched to another — the rail has to come
  // back, or it's invisible in a list that was never scrolled
  useEffect(() => { setNavHidden(false); }, [view]);

  // 🟢 CALLS: one engine, one dock, every view. attachCalls wires the socket
  //    handlers (ring, answer, ICE, upgrade, device-switch); the subscription
  //    mirrors the engine's state into React so the dock + green buttons render.
  const [call, setCall] = useState(getCallState());
  useEffect(() => {
    if (!user || gate !== 'ok') return undefined;
    attachCalls(user.uid);
    /* 👥 the group engine binds to the SAME socket and the same login. It is
       attached unconditionally rather than on demand, because the first thing it
       has to be able to do is receive a ring for a call somebody else started. */
    attachGroupCalls(user.uid, myProfile);

    return subscribeCalls(setCall);
  }, [user, gate]);
  /* 🟥 THE SHELL ONLY RESERVES THE STRIP WHEN THE DOCK IS ACTUALLY IN IT. Detaching
     the call window has to give the conversation its width back — otherwise the
     panel floats away and leaves its hole behind, which looks like a layout bug
     rather than a feature. The mode lives in localStorage next to the dock that
     owns it; this listens for the change rather than passing it down. */
  const [callMode, setCallMode] = useState(() => {
    try { return localStorage.getItem('allo_call_dock_mode') || 'docked'; } catch { return 'docked'; }
  });
  useEffect(() => {
    const read = () => {
      try { setCallMode(localStorage.getItem('allo_call_dock_mode') || 'docked'); } catch { /* private mode */ }
    };
    window.addEventListener('allo-call-mode', read);
    window.addEventListener('storage', read);   // the same account in a second tab
    return () => {
      window.removeEventListener('allo-call-mode', read);
      window.removeEventListener('storage', read);
    };
  }, []);
  const callOpen = call.status !== 'idle' && callMode === 'docked';

  // ── 🔔 WEB NOTIFICATIONS + SOUNDS ─────────────────────────────────────────
  // messages (all types) · vibes social events · missed calls · incoming-call
  // ringtone. attachNotify wires the socket listeners; the context tells it
  // which chats are on screen so an open conversation doesn't ding at you.
  const chatsRef = useRef([]);
  chatsRef.current = chats;
  useEffect(() => {
    if (!user || gate !== 'ok') return undefined;
    attachNotify(user.uid, {
      chatsRef,
      onOpenChat: (chatId) => {
        setView('chats');
        const c = chatsRef.current.find((x) => x.id === chatId);
        if (c) setSelectedChat(c);
      },
    });
  }, [user, gate]);
  // the incoming-call ringtone follows the engine's state
  useEffect(() => {
    if (call.status === 'incoming') notifyIncomingCall(call);
    else stopIncomingNotify();
  }, [call.status, call.callId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 🔴 UNREAD BADGES for the floating rail ────────────────────────────────
  // Chats: how many conversations still hold unread messages (same veto logic the
  // sidebar rows use). Calls: missed incoming calls since the Calls screen was
  // last opened (live records via call-history-sync + the export). Vibes: unread
  // notifications — each one clears as it's tapped in the notifications pane.
  const [callFeed, setCallFeed] = useState([]);
  useEffect(() => {
    if (!user || gate !== 'ok') return undefined;
    attachCallFeed();
    return subscribeCallFeed(setCallFeed);
  }, [user, gate]);
  const [vibesUnread, setVibesUnread] = useState(0);
  useEffect(() => {
    if (!user || gate !== 'ok') return undefined;
    const unsub = watchNotifications(user.uid, (list) =>
      setVibesUnread((list || []).filter((n) => !n.read).length));
    return () => unsub && unsub();
  }, [user, gate]);
  const chatsUnread = React.useMemo(
    () => (chats || []).filter((c) => unreadFor(c, user?.uid, 0, null, myReads[c.id]) > 0).length,
    [chats, user?.uid, myReads],
  );
  const callsUnread = React.useMemo(
    () => missedUnseenCount(callFeed, syncedHistory, user?.uid),
    [callFeed, syncedHistory, user?.uid],
  );
  const badges = { chats: chatsUnread, calls: callsUnread, vibes: vibesUnread };
  // opening the Calls screen shows the full list — that reads it
  const pickView = (v) => { if (v === 'calls') markCallsSeen(); setView(v); };

  // 🟢 a shared_post bubble was clicked in a chat → jump to Vibes and open the post
  const [vibePost, setVibePost] = useState(null);
  useEffect(() => {
    const fn = (e) => {
      const d = e?.detail;
      if (!d || !d.postId) return;
      setVibePost(d);
      setView('vibes');
    };
    // notification clicks deep-link the same way
    const toVibes = () => setView('vibes');
    const toCalls = () => { markCallsSeen(); setView('calls'); };
    window.addEventListener('allo:open-post', fn);
    window.addEventListener('allo:open-vibes', toVibes);
    window.addEventListener('allo:open-calls', toCalls);
    return () => {
      window.removeEventListener('allo:open-post', fn);
      window.removeEventListener('allo:open-vibes', toVibes);
      window.removeEventListener('allo:open-calls', toCalls);
    };
  }, []);

  // tell the notifier which conversations are actually on screen
  useEffect(() => {
    setNotifyContext({
      openChatIds: view === 'chats' ? [selectedChat?.id, secondChat?.id].filter(Boolean) : [],
    });
  }, [view, selectedChat?.id, secondChat?.id]);

  // 🟢 who I've blocked / who blocked me — one listener feeds every guard in the app
  const [blocking, setBlocking] = useState(EMPTY_BLOCKING);
  useEffect(() => {
    if (!user || gate !== 'ok') return;
    const unsub = watchBlocking(user.uid, setBlocking);
    watchChatLocks(user.uid);   // 🔒 the phone's chat locks, respected here
    const unsubReads = watchMyReads(user.uid, setMyReads);
    return () => { if (unsub) unsub(); if (unsubReads) unsubReads(); };
  }, [user, gate]);

  // 🟢 HISTORY SYNC: ask the phone for its full decrypted history and keep it fresh.
  /* 🟥 THE HANDLE ARRIVES LATER THAN THE TEARDOWN CAN. `requestHistory` is async —
     it awaits the device identity and the IndexedDB read before it ever returns —
     so `then(u => unsub = u)` assigns the stopper AFTER React may already have run
     the cleanup. The old cleanup then found `unsub` undefined, called nothing, and
     left behind a live Firestore listener, two window listeners and a five-minute
     heartbeat. StrictMode double-invokes every effect, so in development this
     happened on EVERY mount: two independent sync machines, both writing to one
     `setSyncStatus`, disagreeing with each other — which is exactly what a pill
     flickering between "waiting" and "downloading" looks like.
     A flag closed over by both sides is the whole fix: if teardown got there
     first, stop the machine the moment it exists. */
  const syncRef = useRef(null);
  useEffect(() => {
    if (!user || gate !== 'ok') return undefined;
    let cancelled = false;
    let handle = null;
    let doneTimer = null;
    requestHistory(user.uid, setSyncedHistory, (s, p) => {
      if (cancelled) return;
      setSyncStatus(s);
      setSyncProgress(p || null);
      /* 🟥 THE TIMER OUTLIVED THE STATUS THAT ARMED IT. It was cleared only on
         the way IN to another final state, so a `cached` at t=0 followed by a
         `requested` at t=0.1s wiped the pill four seconds into a sync that was
         still running — and `cached` is precisely what a warm cache emits
         immediately before a refresh. The progress line disappeared mid-ladder
         and looked like nothing was happening. Cancel on EVERY status; re-arm
         only for the two that are genuinely an ending. */
      if (doneTimer) { clearTimeout(doneTimer); doneTimer = null; }
      // "History updated" is worth a moment on screen, not a permanent banner
      if (s === 'synced' || s === 'cached') {
        doneTimer = setTimeout(() => { setSyncStatus(''); setSyncProgress(null); }, 4000);
      }
    }).then((h) => {
      handle = h;
      syncRef.current = h;
      if (cancelled) { try { h.stop(); } catch { /* already gone */ } }
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (doneTimer) clearTimeout(doneTimer);
      if (handle) { try { handle.stop(); } catch { /* already gone */ } }
      syncRef.current = null;
    };
  }, [user, gate, historyEpoch]);

  /* ══ 📮 THE MAILBOX ═══════════════════════════════════════════════════════
     🟥 THIS IS WHAT REPLACES ASKING THE PHONE. Store-and-forward destroys a
        message document ~50ms after the phone caches it, so everything that
        arrived while this tab was shut was gone before it opened — and the only
        way to get it back was a request the phone had to be AWAKE to answer.
        On iOS the operating system will not wake it to answer one.

     🟢 THE PHONE LEAVES A COPY NOW. Draining needs nothing running anywhere, so
        this runs on open and on every return to the tab — phone asleep, phone
        dead, phone in a drawer.

     🟢 AND THE ROWS ARE LIVE DOCUMENT SHAPES, NOT EXPORT SHAPES, so they reach
        the live decoder through the same `isExportedEntry` fork every other row
        does. Nothing here decrypts anything: they were sealed to this device the
        moment it linked, which is exactly why copying them was safe. */
  useEffect(() => {
    if (!user || gate !== 'ok') return undefined;
    let cancelled = false;
    const run = async () => {
      const { byChat, keys } = await drainInbox(user.uid);
      if (cancelled || !keys.length) return;
      if (byChat.size) {
        setSyncedHistory((prev) => {
          const next = { ...(prev || {}) };
          for (const [chatId, rows] of byChat) {
            const have = next[chatId] || [];
            const seen = new Set(have.map((m) => m.id));
            next[chatId] = [...have, ...rows.filter((r) => !seen.has(r.id))];
          }
          return next;
        });
      }
      /* 🟥 CLEARED ONLY AFTER FILING. The phone has already swept the original,
         so an entry dropped before the rows are in state is gone for good. */
      await clearInbox(user.uid, keys);
    };
    run().catch(() => {});
    const onFocus = () => { run().catch(() => {}); };
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, [user, gate]);

  /* 🟥 THE CHAT LIST IS THE ONLY WITNESS TO A MESSAGE THIS BROWSER CANNOT SEE.
     A 1:1 message document is deleted by store-and-forward on the receiving
     phone, so a day of texting moves every chat document's `timestamp` forward
     with nothing readable behind it. The list already sorts by that field — it
     could watch the rows reorder and never thought to ask why. Telling the sync
     what the list saw turns a timer into evidence; `seen` decides what to do
     with it, and asks about each advance once. */
  useEffect(() => {
    if (!chats.length) return;
    let latest = 0;
    for (const c of chats) { const t = lastActivityMs(c); if (t > latest) latest = t; }
    syncRef.current?.seen?.(latest);
  }, [chats]);

  /* 🟢 The Retry in the sidebar's sync pill. Kept behind a ref so the callback
     identity never changes and the sidebar isn't re-rendered by it. */
  const retrySync = useCallback(() => {
    const h = syncRef.current;
    if (h && typeof h.retry === 'function') h.retry();
  }, []);

  /* 👥 …and again once the profile has actually loaded. attachGroupCalls is
     idempotent per socket, so this only refreshes the name and picture the other
     participants see on my tile — which is null on the first attach, because the
     profile fetch has not come back yet. */
  useEffect(() => {
    if (user && gate === 'ok' && myProfile) attachGroupCalls(user.uid, myProfile);
  }, [user, gate, myProfile]);

  const handleLogout = async () => {
    // Take this browser out of the account's Active Sessions on the way out, so the
    // phone's list doesn't keep offering to log out a device that is already gone.
    if (user) await dropSession(user.uid).catch(() => {});
    // 🟥 THE DEVICE IDENTITY SURVIVES A LOG OUT — DO NOT clearIdentity() HERE.
    //    Every message ever sent to this browser was sealed to THIS keypair, addressed
    //    by THIS deviceId. Deleting it doesn't sign you out any harder; it destroys the
    //    only key that can open your own history, so logging back in produced a chat
    //    full of "Sent before this device was linked" — text, photos, voice notes,
    //    documents, locations, all of it — with no way back except asking the phone to
    //    re-export everything. The phone doesn't do this either: getDeviceId() lives in
    //    SecureStore and outlives sign-out by design.
    //    The key IS wiped when the account force-logs this browser out from Active
    //    Sessions (services/session.js → watchSession), because "make that browser
    //    unable to read my messages" is precisely what that button means.
    /* 🟥 THE DECRYPTED MEDIA MUST NOT OUTLIVE THE SESSION. The store keeps the
       plaintext bytes on disk so a reload paints instead of re-downloading —
       which means signing out on a shared machine would leave every photo,
       voice note and document this account had opened sitting in IndexedDB for
       the next person. The identity key deliberately survives a log out; the
       content it opened deliberately does not. */
    /* 🟥 AND THE HISTORY, WHICH THIS PATH FORGOT. Clearing the media store
       left `allo_web_history` — the decrypted message TEXT — on disk, so the
       comment above was true of photos and false of everything said in
       words. wipeLocalData() owns the whole list now; see services/wipeLocal.js. */
    /* 🧳 the wipe below takes this browser's key with it — tell the other devices to stop
          sealing copies for it first (crypto/identity.retireThisDevice) */
    await retireThisDevice().catch(() => {});
    forgetVault();   // 🟢 the DB goes with the wipe; this drops the memory that mirrored it
    forgetSelfIdentity();   // 🟢 …and the name and face this tab was painting from
    await wipeLocalData().catch(() => {});
    await signOut(auth).catch(() => {});
  };

  /** Open a chat by id — used by the group-invite card and by a freshly created
   *  group, which may not be in `chats` yet when the click happens. */
  const openChatId = React.useCallback(async (id) => {
    if (!id) return;
    setView('chats');
    const known = chats.find((c) => c.id === id);
    if (known) { setSelectedChat(known); return; }
    try {
      const snap = await getDoc(doc(db, 'chats', id));
      if (snap.exists()) setSelectedChat({ id, ...snap.data() });
    } catch {}
  }, [chats]);

  // start (or open) a 1:1 chat with a searched user — deterministic chat id (mobile parity)
  const startChat = async (targetUser) => {
    const ids = [user.uid, targetUser.uid].sort();
    const chatId = ids.join('_');
    const ref = doc(db, 'chats', chatId);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        participants: ids,
        isGroup: false,
        createdAt: serverTimestamp(),
        lastMessageTime: serverTimestamp(),
      }, { merge: true });
    }
    setSelectedChat({ id: chatId, participants: ids, isGroup: false, ...(snap.data() || {}) });
  };

  /* 🟥 THE SAME SCREEN THE PHONE SHOWS, not the word "Loading…" in grey. This
     is the identical moment on both clients — auth restoring, the session row
     being looked up — and it was the one place the two launches looked nothing
     like each other. `BootGreetings` reads the generated twin of the phone's
     own greeting list, so neither client can grow a language the other has no
     word for. */
  if (user === undefined || (user && gate === 'checking')) {
    return <BootGreetings />;
  }
  if (user === null) return <QRLogin />;
  if (gate !== 'ok') return <LoginPending user={user} onApproved={() => setGate('ok')} />;
  /* 🟥 ABOVE THE APP, NOT BESIDE IT. A locked vault means the caches cannot be
     read at all, so rendering the chat list behind this would paint a wall of
     failures and invite the user to fix each one. */
  if (vaultAsk) {
    return (
      <VaultGate
        mode={vaultAsk}
        onDone={() => { setVaultAsk(null); setHistoryEpoch((e) => e + 1); }}
        onSkip={() => setVaultAsk(null)}
      />
    );
  }

  if (view === 'vibes') {
    return (
      <div className={`allo-app ${callOpen ? 'call-open' : ''}`}>
        <div className="allo-shell" style={{ gridTemplateColumns: '1fr' }}>
          <div className="nav-host">
            {/* the rail is handed to VibesScreen so it can host it over the
                analytics column — see the note at its render site there */}
            <VibesScreen
              myUid={user.uid} myProfile={myProfile} chats={chats}
              onOpenChatId={openChatId}
              sharedPostId={vibePost} onSharedPostViewed={() => setVibePost(null)}
              onListScroll={(dir) => setNavHidden(dir === 'down')}
              navRail={(
                <NavRail
                  view={view} onPick={pickView} hidden={navHidden}
                  badges={badges} myProfile={myProfile}
                />
              )}
            />
          </div>
        </div>
        {/* 🟢 calls follow you into Vibes — outgoing, incoming and active all render
            here too, and the squeeze narrows the 3-pane grid (reels included) */}
        <CallDock call={call} chats={chats} myUid={user.uid} />
        <GroupCallDock myUid={user.uid} myProfile={myProfile} chats={chats} />
        {/* ⭐ 🟥 OUTSIDE THE DOCK, ON PURPOSE. The dock renders from `call.status`,
            which teardown sets to 'idle' — so a card rendered inside it would be
            destroyed in the same tick it was created. This is driven by
            `lastEnded`, the one field teardown deliberately preserves.
  
            🟢 It appears in BOTH shells (the Vibes view returns early and never
            reaches the one below) because a call can end while the person is
            looking at either, and a prompt that depends on which tab you happened
            to be in is a prompt that mostly does not appear. */}
        {call?.lastEnded && (
          <CallRatingCard
            key={call.lastEnded.callId}
            call={call.lastEnded}
            onClose={clearLastEndedCall}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`allo-app ${callOpen ? 'call-open' : ''}`}>
      <div
        className={`allo-shell ${selectedChat ? 'has-chat' : ''} ${secondChat ? 'split' : ''}`}
        ref={shellRef}
      >
        <div className="nav-host">
        {view === 'calls' ? (
          <CallsPanel myUid={user.uid} chats={chats} syncedHistory={syncedHistory} />
        ) : view === 'profile' ? (
          <ProfilePanel myUid={user.uid} />
        ) : (
        <Sidebar
          blocking={blocking}
          myUid={user.uid}
          myProfile={myProfile}
          chats={chats}
          selectedChatId={selectedChat?.id}
          onSelectChat={setSelectedChat}
          onStartChat={startChat}
          onOpenChatId={openChatId}
          onHome={() => { setSelectedChat(null); setSecondChat(null); }}
          draggableRows={wide}
          onLogout={handleLogout}
          syncStatus={syncStatus}
          syncProgress={syncProgress}
          onRetrySync={retrySync}
          syncedHistory={syncedHistory}
          onListScroll={(dir) => setNavHidden(dir === 'down')}
          myReads={myReads}
        />
        )}
        {/* 🟢 the floating section rail: icons only, hides as the chat list is scrolled
            deeper, returns on the way back up (Sidebar reports direction) */}
        <NavRail view={view} onPick={pickView} hidden={view === 'chats' && navHidden} badges={badges} myProfile={myProfile} />
        </div>
        {/* the draggable border between the list and the conversation. It's absolutely
            positioned over the grid line rather than being a grid column of its own, so
            the existing template — and every measurement inside both panes — is
            untouched whether or not it's there. */}
        <Resizer
          targetRef={shellRef}
          cssVar="--sidebar-w"
          storageKey="allo_sidebar_w"
          min={260}
          max={(box) => Math.min(760, box.width * 0.55)}
          vertical="sidebar-edge"
        />

        {selectedChat ? (
          secondChat ? (
            <div className="chat-split" ref={splitRef}>
              <ErrorBoundary key={selectedChat.id}>
                <ChatLockGate chat={chats.find((c) => c.id === selectedChat.id) || selectedChat} onBack={() => setSelectedChat(null)}>
                <ChatWindow
                  blocking={blocking}
                  chat={chats.find((c) => c.id === selectedChat.id) || selectedChat}
                  myUid={user.uid}
                  myProfile={myProfile}
                  syncedHistory={syncedHistory}
                  onBack={() => setSelectedChat(null)}
                  onClose={() => { setSelectedChat(secondChat); setSecondChat(null); }}
                  onDropChat={(id) => dropChat(id, 'a')}
                  onForward={(m) => setForwarding(m)}
                  onOpenMedia={setViewer}
                  onOpenChatId={openChatId}
                  allChats={chats}
                />
                </ChatLockGate>
              </ErrorBoundary>

              <Resizer
                targetRef={shellRef}
                measureRef={splitRef}
                cssVar="--split-a"
                storageKey="allo_split_a"
                unit="%"
                /* proportional, not fixed: on a narrow split a 360px floor would
                   have left the other pane 120px wide */
                min={(box) => box.width * 0.3}
                max={(box) => box.width * 0.7}
                vertical="split-edge"
              />

              <ErrorBoundary key={secondChat.id}>
                <ChatLockGate chat={chats.find((c) => c.id === secondChat.id) || secondChat} onBack={() => setSecondChat(null)}>
                <ChatWindow
                  blocking={blocking}
                  chat={chats.find((c) => c.id === secondChat.id) || secondChat}
                  myUid={user.uid}
                  myProfile={myProfile}
                  syncedHistory={syncedHistory}
                  onBack={() => setSecondChat(null)}
                  onClose={() => setSecondChat(null)}
                  onDropChat={(id) => dropChat(id, 'b')}
                  onForward={(m) => setForwarding(m)}
                  onOpenMedia={setViewer}
                  onOpenChatId={openChatId}
                  allChats={chats}
                />
                </ChatLockGate>
              </ErrorBoundary>
            </div>
          ) : (
            <ErrorBoundary key={selectedChat.id}>
              <ChatLockGate chat={chats.find((c) => c.id === selectedChat.id) || selectedChat} onBack={() => setSelectedChat(null)}>
              <ChatWindow
                  blocking={blocking}
                chat={chats.find((c) => c.id === selectedChat.id) || selectedChat}
                myUid={user.uid}
                myProfile={myProfile}
                syncedHistory={syncedHistory}
                onBack={() => setSelectedChat(null)}
                onDropChat={(id) => dropChat(id, 'single')}
                onForward={(m) => setForwarding(m)}
                onOpenMedia={setViewer}
                onOpenChatId={openChatId}
                allChats={chats}
              />
              </ChatLockGate>
            </ErrorBoundary>
          )
        ) : (
          <WelcomePane />
        )}
      </div>

      {forwarding && (
        <ForwardModal
          blocking={blocking}
          payload={forwarding}
          chats={chats}
          myUid={user.uid}
          myName={myProfile?.fullName || myProfile?.username || 'Web'}
          onClose={() => setForwarding(null)}
          onSent={(n) => console.log(`[forward] delivered to ${n} chat(s)`)}
        />
      )}
      {viewer && <MediaViewer view={viewer} onClose={() => { pmRelockItems(); setViewer(null); }} />}
      {/* 🟢 the call dock — squeezes the shell exactly like the chat tools panel */}
      <CallDock call={call} chats={chats} myUid={user.uid} />
      <GroupCallDock myUid={user.uid} myProfile={myProfile} chats={chats} />
      {/* ⭐ 🟥 OUTSIDE THE DOCK, ON PURPOSE. The dock renders from `call.status`,
          which teardown sets to 'idle' — so a card rendered inside it would be
          destroyed in the same tick it was created. This is driven by
          `lastEnded`, the one field teardown deliberately preserves.

          🟢 It appears in BOTH shells (the Vibes view returns early and never
          reaches the one below) because a call can end while the person is
          looking at either, and a prompt that depends on which tab you happened
          to be in is a prompt that mostly does not appear. */}
      {call?.lastEnded && (
        <CallRatingCard
          key={call.lastEnded.callId}
          call={call.lastEnded}
          onClose={clearLastEndedCall}
        />
      )}
    </div>
  );
}
